// React
import React from "react"
import { isHoliday } from "feiertagejs"
import { isHolidayToday } from "utils/holidays"
import { useRouter } from "next/router"

// dayjs
import dayjs from "utils/dates"

// nextjs
import Link from "next/link"

import {
  Alert,
  Col,
  Divider,
  FloatButton,
  Row,
  Segmented,
  Space,
  Tag,
  Typography,
  Image,
  Affix,
} from "antd"
import {
  PhoneFilled,
  ClockCircleFilled,
  CheckCircleFilled,
  MinusCircleFilled,
} from "@ant-design/icons"
import UserInfo from "components/UserInfoMenu"

// styling
import styled from "@emotion/styled"

// configs
import config from "config"

// utils
import { matchTimeRanges, days } from "utils"

// context
import { useResto } from "store/resto"
import { useSettings } from "store/settings"
import { useTranslation } from "react-i18next"
import { FunnelProvider, useFunnel } from "store/funnel"

const App = styled.div`
  background-color: ${config.color};
`

const HeaderTitle = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: column;
  padding: 16px;
  padding-top: 0;
  padding-bottom: 0;

  @media (max-width: 820px) {
    align-items: flex-start;
  }

  h1,
  h2 {
    color: white;
  }

  h1 {
    letter-spacing: 2px;
    margin-bottom: 8px;
  }

  h2 {
    margin-top: 0px !important;
    font-size: 14px;
    letter-spacing: 2px;
  }
`

const Info = styled.div`
  padding: 16px;
  padding-bottom: 0px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
`

const Infouser = styled.div`
  padding: 16px;

  display: flex;
  justify-content: space-between;
  flex-wrap: no-wrap;
  gap: 20px;

  transition: background-color 0.1s;

  &.affixed {
    background: rgba(239, 239, 239, 1);
    background: white;
    border-bottom: 1px solid black;

    margin-left: 16px;
    margin-right: 16px;
    border-bottom-right-radius: 8px;
    border-bottom-left-radius: 8px;

    @media (max-width: 768px) {
      margin-left: 0px;
      margin-right: 0px;
    }
  }
`

const View = styled.div`
  min-height: 1000px;

  padding: 16px 16px;
`

const Footer = styled.footer`
  padding: 8px 8px;

  text-align: right;

  padding-bottom: 300px;
`

const MainLayout = ({ children }) => {
  const restoSettings = useResto()
  const { settings, setSettings } = useSettings()
  const { t, i18n } = useTranslation()
  const funnel = useFunnel()

  const router = useRouter()

  const [affixed, setAffixed] = React.useState(false)

  const holidayToday = React.useMemo(() => {
    return isHolidayToday()
  }, [])

  const userInteractedWithApp = React.useRef(false)

  const handleUserInteraction = React.useCallback(() => {
    if (!userInteractedWithApp.current && funnel.initialized) {
      console.log("User interacted with the app")
      userInteractedWithApp.current = true
      funnel.add("page_interaction")
    }
  }, [funnel])

  const isRoot = router.pathname === "/"
  const isMenu = router.pathname === "/menu"

  return (
    <App onClick={handleUserInteraction} onTouchStart={handleUserInteraction}>
      <FloatButton.BackTop visibilityHeight={800} />

      {restoSettings && restoSettings.banner === true && isRoot && (
        <Alert
          message={restoSettings.banner_title}
          description={restoSettings.banner_description}
          type="warning"
          closable
          banner
        />
      )}
      {config.banner && isRoot && (
        <Image
          alt="banner"
          preview={false}
          style={{
            objectFit: "cover",
          }}
          height={300}
          width="100%"
          src={`restos/${config.key}/banner.jpg`}
        />
      )}

      <Info>
        <Space wrap>
          {isRoot &&
            restoSettings &&
            (restoSettings.restaurent_is_closed ? (
              <Tag icon={<MinusCircleFilled />} color="error">
                <Typography.Text strong>{t("closed_excl")}</Typography.Text>
              </Tag>
            ) : holidayToday ? (
              <Tag icon={<ClockCircleFilled />} color="warning">
                <Typography.Text strong>
                  {t("potentially_open")}
                </Typography.Text>
              </Tag>
            ) : matchTimeRanges(config.hours) ? (
              <Tag icon={<CheckCircleFilled />} color="success">
                <Typography.Text strong>{t("open_excl")}</Typography.Text>
              </Tag>
            ) : (
              <Tag icon={<MinusCircleFilled />} color="error">
                <Typography.Text strong>{t("closed_excl")}</Typography.Text>
              </Tag>
            ))}

          {isRoot && (
            <Tag icon={<PhoneFilled />}>
              <Space wrap>
                {config.phones.map((number) => (
                  <a key={number} href={`tel:${number}`}>
                    {number}
                  </a>
                ))}
              </Space>
            </Tag>
          )}

          {config.languages.length > 1 && (
            <Segmented
              size="small"
              value={settings.language}
              options={config.languages.map((lang) => ({
                label: `${
                  { de: "🇩🇪", en: "🇬🇧", fr: "🇫🇷", nl: "🇳🇱" }[lang] || ""
                } ${lang}`,
                value: lang,
              }))}
              onChange={(value) => {
                setSettings({ ...settings, language: value })
              }}
            />
          )}
        </Space>
      </Info>

      <HeaderTitle>
        <Typography.Title level={1} style={{ color: config.textColor }}>
          {config.title}
        </Typography.Title>
        <Typography.Title style={{ color: config.textColor }} level={2}>
          {config.subtitle}
        </Typography.Title>
      </HeaderTitle>

      {!isMenu && (
        <Affix offsetTop={0} onChange={(affixed) => setAffixed(affixed)}>
          <Infouser className={affixed ? "affixed" : ""}>
            <Typography.Text
              strong
              style={{
                color: affixed ? "black" : "transparent",
                transition: "color 0.3s",
              }}
            >
              {config.title}
            </Typography.Text>

            <UserInfo />
          </Infouser>
        </Affix>
      )}

      <View>{children}</View>

      <Footer>
        {isRoot && (
          <>
            <Typography.Paragraph>
              <Typography.Text style={{ color: config.textColor }} strong>
                {t("owner")} {config.owner}
              </Typography.Text>
              <br />
              <Typography.Text style={{ color: config.textColor }}>
                {config.address.line1}
              </Typography.Text>
              <br />
              <Typography.Text style={{ color: config.textColor }}>
                {config.address.line2}
              </Typography.Text>
              <br />
              <Typography.Text style={{ color: config.textColor }}>
                {config.address.zip} {config.address.city}
              </Typography.Text>
            </Typography.Paragraph>
            <Divider />
            <Typography.Paragraph>
              <Space>
                <ClockCircleFilled style={{ color: config.textColor }} />
                <Typography.Text style={{ color: config.textColor }} strong>
                  {t("opening_hours")}
                </Typography.Text>
              </Space>
            </Typography.Paragraph>
            <Row wrap justify="end" gutter={16}>
              {Object.entries(config.hours).map(([index, item]) => (
                <Col key={index} span={24}>
                  <Space>
                    <Typography.Text style={{ color: config.textColor }} strong>
                      {dayjs()
                        .day(Number(index))
                        .locale(i18n.resolvedLanguage)
                        .format("ddd")}
                      :
                    </Typography.Text>
                    {item ? (
                      <Space>
                        {item.map(({ start_time, end_time }) => (
                          <Typography.Text
                            style={{ color: config.textColor }}
                            key={start_time}
                          >{`${dayjs(start_time).format("HH:mm")} - ${dayjs(
                            end_time
                          ).format("HH:mm")}`}</Typography.Text>
                        ))}
                      </Space>
                    ) : (
                      <Typography.Text style={{ color: config.textColor }}>
                        {t("closed")}
                      </Typography.Text>
                    )}
                  </Space>
                </Col>
              ))}
            </Row>
            <Divider />
            <Link href="/terms" style={{ color: config.textColor }}>
              <Typography.Text style={{ color: config.textColor }}>
                {t("legal_notice")}
              </Typography.Text>
            </Link>
            <Divider />
          </>
        )}
        <Typography.Paragraph>
          {/* <Typography.Text style={{ color: config.textColor }} strong>
            App provided by
          </Typography.Text>
          <br />
          <Typography.Text style={{ color: config.textColor }}>
            Foodrestos
          </Typography.Text>
          <br /> */}
          <Typography.Text style={{ color: config.textColor }}>
            Version: {process.env.version} (
            {dayjs(process.env.buildDate).format("MMM YYYY")})
          </Typography.Text>
          {/* <br />
          <Typography.Text style={{ color: config.textColor }}>
            www.foodrestos.de
          </Typography.Text> */}
        </Typography.Paragraph>
      </Footer>
    </App>
  )
}

const Main = ({ ...props }) => (
  <FunnelProvider>
    <MainLayout {...props} />
  </FunnelProvider>
)

export default Main
